/**
 * The ledger wire shapes: accounts and the exact cash entries (P4-T1).
 *
 * Sources: `IMPLEMENTATION-CONTRACTS.md` §3 (canonical integers, the individual money bound,
 * business dates, text lengths), §5 (the resource envelope and the exact ordinary-create field
 * list) and §4 (revisions as positive canonical integer strings).
 *
 * Two properties of these schemas carry most of the acceptance: every object sets
 * `additionalProperties: false`, so an unknown property — including a `currency` on an account
 * rename, or a numeric `signedMinor` — is a rejection rather than something silently dropped; and
 * money is a **string** with a canonical pattern, so a JSON number is never a money value
 * (`strictAjvOptions` disables coercion, so `"100"` is not a number and `100` is not a string
 * either).
 *
 * They deliberately carry no `$id`. A `$id` would have to be registered exactly once with the
 * API's Ajv instance, and these objects are composed into several route schemas (a request body,
 * a single-resource envelope, a collection envelope); without one, each route compiles its own
 * copy and nothing can collide. The four **response** envelopes the browser validates are the
 * exception, and are named and `$id`-bearing at the end of this file
 * (`ledgerAccountEnvelopeSchema` and its siblings): the client-side precompiled set in
 * `src/generated/validators.ts` is generated from fixed `$id`s, and that set is what P4-T2 added
 * and regenerates.
 */
export const LEDGER_CURRENCIES = ['BRL', 'CAD', 'USD'];
/** `0`, digits without leading zeros, or minus followed by positive digits (§3). */
export const CANONICAL_MINOR_PATTERN = '^(?:0|[1-9][0-9]*|-[1-9][0-9]*)$';
/** A positive canonical integer string: revision `"1"`, `"2"`, … (§3, §4). */
export const POSITIVE_INTEGER_PATTERN = '^[1-9][0-9]*$';
/** Strict Gregorian `YYYY-MM-DD`, `1900-01-01`..`2199-12-31` (§3). */
export const BUSINESS_DATE_PATTERN = '^(?:19|20|21)[0-9]{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$';
/** Lowercase UUID text, validated before any resolution (§3). */
export const UUID_PATTERN = '^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$';
const businessDate = { type: 'string', pattern: BUSINESS_DATE_PATTERN };
const nullableBusinessDate = {
    type: ['string', 'null'],
    pattern: BUSINESS_DATE_PATTERN,
};
const canonicalMinor = { type: 'string', pattern: CANONICAL_MINOR_PATTERN };
const positiveInteger = { type: 'string', pattern: POSITIVE_INTEGER_PATTERN };
const currency = { type: 'string', enum: [...LEDGER_CURRENCIES] };
/**
 * The payment pair's state, as a leg's own entry read carries it (P6-T3).
 *
 * A transfer leg moves money only while its pair is **completed** (`isActualEligible`), so the
 * entry read states the state rather than leaving a client to infer it from an id it cannot
 * resolve. Declared here, above `entrySchema`, because the entry read uses it as well as the
 * transfer schemas below.
 */
export const TRANSFER_STATUSES = ['planned', 'completed'];
const transferStatus = { type: 'string', enum: [...TRANSFER_STATUSES] };
const nullableTransferStatus = { type: ['string', 'null'], enum: [...TRANSFER_STATUSES, null] };
/** Aggregate money: `{currency, minorUnits}`, always exact minor units (§3). */
export const moneySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['currency', 'minorUnits'],
    properties: { currency, minorUnits: canonicalMinor },
};
/** The resource envelope's `meta` member (§5). */
export const ledgerMetaSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['requestId', 'asOf', 'businessToday', 'timeZone', 'dataRevision'],
    properties: {
        requestId: { type: 'string', minLength: 1, maxLength: 64 },
        /** The technical instant, UTC ISO millisecond text — never a substitute for a business DATE. */
        asOf: { type: 'string', minLength: 1, maxLength: 64 },
        businessToday: businessDate,
        timeZone: { type: 'string', minLength: 1, maxLength: 64 },
        dataRevision: positiveInteger,
    },
};
/** An account as the API returns it: metadata plus its derived balance at businessToday. */
export const accountSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['id', 'name', 'currency', 'kind', 'archived', 'revision', 'balance'],
    properties: {
        id: { type: 'string', pattern: UUID_PATTERN },
        name: { type: 'string', minLength: 1, maxLength: 120 },
        currency,
        kind: { type: 'string', enum: ['cash', 'card'] },
        archived: { type: 'boolean' },
        revision: positiveInteger,
        balance: {
            type: 'object',
            additionalProperties: false,
            required: ['currency', 'minorUnits', 'negative'],
            properties: {
                currency,
                minorUnits: canonicalMinor,
                /** True when the balance is below zero: allowed, and marked rather than clamped. */
                negative: { type: 'boolean' },
            },
        },
    },
};
/** An entry as the API returns it (§5, "Entry read"). */
export const entrySchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'accountId',
        'type',
        'signedMinor',
        'currency',
        'description',
        'transactionDate',
        'dueDate',
        'paid',
        'revision',
        'effectiveDate',
        'installmentGroupId',
        'installmentOrdinal',
        'transferStatus',
        'categoryId',
        'canceled',
    ],
    properties: {
        id: { type: 'string', pattern: UUID_PATTERN },
        accountId: { type: 'string', pattern: UUID_PATTERN },
        type: { type: 'string', enum: ['income', 'expense', 'opening', 'transfer'] },
        signedMinor: canonicalMinor,
        currency,
        description: { type: 'string', minLength: 1, maxLength: 500 },
        transactionDate: nullableBusinessDate,
        dueDate: nullableBusinessDate,
        paid: { type: ['boolean', 'null'] },
        revision: positiveInteger,
        /** Derived from the row's own date; it is never stored a second time. */
        effectiveDate: businessDate,
        /**
         * The installment group this row belongs to, and its ordinal within it (P5-T2). Both are
         * `null` on a row that is not an installment; the ordinal is assigned once at generation and is
         * never released, so a deleted member leaves a visible gap rather than renumbering the schedule.
         */
        installmentGroupId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        installmentOrdinal: { type: ['integer', 'null'], minimum: 1, maximum: 120 },
        /**
         * The payment pair's state when this row is one of its legs (P6-T3); `null` on every other
         * row. A leg moves money only while its pair is `completed`, so a client that derives a
         * figure from the entry read has to be told — the pair's id alone cannot answer it.
         */
        transferStatus: nullableTransferStatus,
        /**
         * The one optional category this ordinary row carries (P7-T1), or `null` for the first-class
         * *uncategorized* state. A special row (an opening, a transfer leg) carries none.
         */
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        /**
         * The cancellation marker. A canceled row stays fully readable — it is returned by both the
         * resource read and the collection read — and it contributes zero to every figure, because
         * the one eligibility rule refuses it (`isActualEligible`).
         */
        canceled: { type: 'boolean' },
    },
};
/**
 * Creating an account, optionally with its opening.
 *
 * An opening is signed or zero (§3) and is *creation-only composition*: there is no separate
 * opening route on this surface, and `opening.create/update/delete` remains a later card's
 * central action.
 */
export const accountCreateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['name', 'currency', 'kind'],
    properties: {
        name: { type: 'string', minLength: 1, maxLength: 120 },
        currency,
        kind: { type: 'string', enum: ['cash', 'card'] },
        opening: {
            type: 'object',
            additionalProperties: false,
            required: ['date', 'signedMinor'],
            properties: { date: businessDate, signedMinor: canonicalMinor },
        },
    },
};
/**
 * Rename. `currency` and `kind` are absent on purpose: they are not "unknown properties dropped",
 * they are properties this surface has no way to set, so a request carrying one is rejected.
 */
export const accountPatchRequestSchema = {
    type: 'object',
    additionalProperties: false,
    minProperties: 1,
    properties: {
        name: { type: 'string', minLength: 1, maxLength: 120 },
        /**
         * The reversible hide/unhide (P7-T1). It is visibility only: history and totals survive it,
         * because an archived account's entries are untouched and still counted. It is a boolean
         * rather than one-way actions, so unhide is the same field with the other value.
         */
        archived: { type: 'boolean' },
    },
};
/**
 * Replacing an account's opening (P7-T1).
 *
 * The opening is signed or zero (§3), and this action **replaces**: the account's active opening
 * is canceled and a new one is created in the same transaction, with the cancellation's
 * provenance recorded. At most one active opening per account survives, whatever order concurrent
 * replacements arrive in, because the write serializes on the owner and the schema's partial
 * unique index is the final guarantee.
 */
export const accountOpeningRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['date', 'signedMinor'],
    properties: { date: businessDate, signedMinor: canonicalMinor },
};
/** The ordinary-create fields, verbatim from §5. Nothing else is accepted. */
export const entryCreateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'accountId',
        'type',
        'signedMinor',
        'description',
        'transactionDate',
        'dueDate',
    ],
    properties: {
        accountId: { type: 'string', pattern: UUID_PATTERN },
        type: { type: 'string', enum: ['income', 'expense'] },
        signedMinor: canonicalMinor,
        description: { type: 'string', minLength: 1, maxLength: 500 },
        transactionDate: businessDate,
        dueDate: businessDate,
        paid: { type: 'boolean' },
        counterparty: { type: 'string', maxLength: 200 },
        notes: { type: 'string', maxLength: 5000 },
        categoryId: { type: 'string', pattern: UUID_PATTERN },
    },
};
/**
 * Editing an entry, and the cash pay.
 *
 * There is no alternate pay route (§5, "Cash paid/due-date change is this same PATCH, not an
 * alternate pay route"), and the account, the type and the currency are not writable here — an
 * entry's currency is its account's currency, and moving an entry between accounts is not a
 * metadata edit.
 */
export const entryPatchRequestSchema = {
    type: 'object',
    additionalProperties: false,
    minProperties: 1,
    properties: {
        signedMinor: canonicalMinor,
        description: { type: 'string', minLength: 1, maxLength: 500 },
        transactionDate: businessDate,
        dueDate: businessDate,
        paid: { type: 'boolean' },
        counterparty: { type: 'string', maxLength: 200 },
        notes: { type: 'string', maxLength: 5000 },
        /**
         * The one optional category, and the only way to *unset* it: `null` is a deliberate
         * *uncategorized* value, while omitting the field leaves the assignment alone.
         */
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
    },
};
/** The `accountId` a list read is scoped to, the optional type filter, and a bounded page size. */
export const entryListQuerySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['accountId'],
    properties: {
        accountId: { type: 'string', pattern: UUID_PATTERN },
        type: { type: 'string', enum: ['income', 'expense', 'opening', 'transfer'] },
        // A wire string, validated as one and bounded-parsed afterwards: Ajv coercion is off, so a
        // query value never becomes a number on the way in (`IMPLEMENTATION-CONTRACTS.md` §2).
        limit: { type: 'string', pattern: '^[1-9][0-9]{0,2}$' },
        /**
         * Whether canceled rows are included. Canceled rows are never deleted and remain readable
         * (P7-T1); this flag is how a caller asks for them in a collection read, and it is off by
         * default because the ordinary view is the live ledger.
         */
        includeCanceled: { type: 'string', enum: ['true', 'false'] },
        /** One category's entries, or `uncategorized` for the entries that carry none. */
        categoryId: { type: 'string', pattern: `^(?:${UUID_PATTERN.slice(1, -1)}|uncategorized)$` },
    },
};
/** Build the response schema for a single-resource envelope around `item`. */
export function resourceEnvelopeSchema(item) {
    return {
        type: 'object',
        additionalProperties: false,
        required: ['data', 'meta'],
        properties: { data: item, meta: ledgerMetaSchema },
    };
}
/** Build the response schema for a collection envelope whose `data` is `collection`. */
export function collectionEnvelopeSchema(collection) {
    return {
        type: 'object',
        additionalProperties: false,
        required: ['data', 'meta'],
        properties: { data: collection, meta: ledgerMetaSchema },
    };
}
/**
 * The named response schemas the **browser** validates with (P4-T2).
 *
 * `resourceEnvelopeSchema`/`collectionEnvelopeSchema` above compose a fresh object per
 * route, which is what Fastify wants; they cannot be precompiled, because Ajv's
 * standalone generator needs a schema registered under a stable `$id` and a fixed
 * export. These four are that fixed set: one per read the monthly UI performs. They are
 * assembled from the same members the route schemas use, so "what the API serializes"
 * and "what the browser accepts" cannot describe two different shapes.
 *
 * They are also why the UI needs no `'unsafe-eval'`: the bundle calls the precompiled
 * validators in `src/generated/validators.ts` and never compiles a schema
 * (`P3-T2e` follow-up; `strict-ajv.ts`). Regenerate with `npm run generate` after any
 * change here — `validator.test.ts` fails if the committed module drifts.
 */
/** One account, in the resource envelope. */
export const ledgerAccountEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-account-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerAccountEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: accountSchema, meta: ledgerMetaSchema },
};
/** One owner's accounts, in the collection envelope. */
export const ledgerAccountsEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-accounts-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerAccountsEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: {
        data: {
            type: 'object',
            additionalProperties: false,
            required: ['accounts'],
            properties: { accounts: { type: 'array', maxItems: 500, items: accountSchema } },
        },
        meta: ledgerMetaSchema,
    },
};
/** One entry, in the resource envelope. */
export const ledgerEntryEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-entry-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerEntryEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: entrySchema, meta: ledgerMetaSchema },
};
/** One account's entries, in the collection envelope. */
export const ledgerEntriesEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-entries-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerEntriesEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: {
        data: {
            type: 'object',
            additionalProperties: false,
            required: ['entries'],
            properties: { entries: { type: 'array', maxItems: 200, items: entrySchema } },
        },
        meta: ledgerMetaSchema,
    },
};
/* ------------------------------------------------------------------ installments --- */
/**
 * The installment wire shapes (P5-T2).
 *
 * Sources: `IMPLEMENTATION-CONTRACTS.md` §5 (the central `installments.create`/`installments.edit`
 * actions and the `POST /mutation-previews` → `POST /mutations` review-required spine), §3
 * (installments 1..120, exact minor units, business DATEs) and §4 (a preview carries the exact
 * selection, the dependency revisions and the eligibility at `businessToday`, expires, and is
 * single-use).
 *
 * Two properties carry the acceptance and are enforced by the shape rather than by a handler:
 *
 *   - every object sets `additionalProperties: false`, so the fields a forward edit must **not**
 *     propagate — `dueDate`, `paid`, `notes` — are a rejection when submitted, not something a
 *     handler silently drops (INST-05: dates/status/notes stay individual);
 *   - `installments.create` and `installments.edit` have **different** allowed fields, expressed as
 *     a `oneOf` whose `action` acts as the discriminator, so the create path cannot carry a forward
 *     selection and the edit path cannot carry a count.
 */
/** An installment ordinal or count: a bounded ordinary integer, not a money value (§3). */
const installmentOrdinal = { type: 'integer', minimum: 1, maximum: 120 };
const installmentCount = { type: 'integer', minimum: 0, maximum: 120 };
const description = { type: 'string', minLength: 1, maxLength: 500 };
const uuid = { type: 'string', pattern: UUID_PATTERN };
/** The vocabulary a member's own state is reported with; derived, never stored twice. */
export const INSTALLMENT_MEMBER_STATES = [
    'pending',
    'future',
    'paid',
    'effective',
    'deleted',
];
/** Why a member is outside a change's scope (INST-04, INST-06). */
export const INSTALLMENT_EXCLUSION_REASONS = [
    'earlier',
    'paid-cash',
    'card-effective',
    'already-deleted',
];
/** One scheduled member, as a group read returns it. */
export const installmentMemberSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['entryId', 'ordinal', 'dueDate', 'signedMinor', 'currency', 'accountId', 'revision', 'state'],
    properties: {
        entryId: uuid,
        ordinal: installmentOrdinal,
        dueDate: businessDate,
        signedMinor: canonicalMinor,
        currency,
        accountId: uuid,
        revision: positiveInteger,
        state: { type: 'string', enum: [...INSTALLMENT_MEMBER_STATES] },
    },
};
/**
 * One installment group (P5-T2).
 *
 * `scheduledCount` is the count generation produced and is **constant**: a deleted member stops
 * contributing (`activeCount` falls, `total` falls) but the ordinal is retained and the schedule is
 * still "of 12" — the operator's reading of the plan's ambiguity, recorded on `t_4bda0541`.
 */
export const installmentGroupSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'defaultAccountId',
        'currency',
        'revision',
        'scheduledCount',
        'activeCount',
        'total',
        'members',
    ],
    properties: {
        id: uuid,
        /** The generation default only: each member's own account is authoritative (INST-02). */
        defaultAccountId: uuid,
        currency,
        revision: positiveInteger,
        scheduledCount: installmentOrdinal,
        activeCount: installmentCount,
        total: moneySchema,
        members: { type: 'array', maxItems: 120, items: installmentMemberSchema },
    },
};
/** A member as a **preview** shows it: no identity yet when the group does not exist. */
export const installmentPreviewMemberSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['ordinal', 'dueDate', 'signedMinor', 'currency', 'accountId'],
    properties: {
        ordinal: installmentOrdinal,
        dueDate: businessDate,
        signedMinor: canonicalMinor,
        currency,
        accountId: uuid,
        /** The existing row this preview would change; `null` for a member that does not exist yet. */
        entryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
    },
};
/** A member outside the previewed scope, with the plan's own reason. */
export const installmentExcludedSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['entryId', 'reason'],
    properties: {
        entryId: uuid,
        reason: { type: 'string', enum: [...INSTALLMENT_EXCLUSION_REASONS] },
    },
};
/**
 * A stored mutation preview (`POST /mutation-previews`, §4/§5).
 *
 * It carries the exact selection, the dependency revision it was computed at, the eligibility at
 * `businessToday` (as the included and excluded sets), an expiry, and the revised total — so the
 * commit that follows can be refused as stale instead of recomputing silently.
 */
export const mutationPreviewSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'action',
        'groupId',
        'revision',
        'businessToday',
        'expiresAt',
        'scheduledCount',
        'activeCount',
        'total',
        'members',
        'affectedEntryIds',
        'excluded',
    ],
    properties: {
        id: uuid,
        action: {
            type: 'string',
            enum: ['installments.create', 'installments.edit', 'installments.delete'],
        },
        groupId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        /** The target group's revision at preview time; `null` for a creation. */
        revision: { type: ['string', 'null'], pattern: POSITIVE_INTEGER_PATTERN },
        /** The business day the eligibility was computed at — changing Today invalidates the preview. */
        businessToday: businessDate,
        expiresAt: { type: 'string', minLength: 1, maxLength: 64 },
        scheduledCount: installmentCount,
        activeCount: installmentCount,
        total: moneySchema,
        members: { type: 'array', maxItems: 120, items: installmentPreviewMemberSchema },
        affectedEntryIds: { type: 'array', maxItems: 120, items: uuid },
        excluded: { type: 'array', maxItems: 120, items: installmentExcludedSchema },
    },
};
/** `installments.create`: the purchase and its count. The anchor is the purchase's own due date. */
export const installmentsCreateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'action',
        'accountId',
        'type',
        'signedMinor',
        'description',
        'transactionDate',
        'dueDate',
        'count',
    ],
    properties: {
        action: { type: 'string', enum: ['installments.create'] },
        accountId: uuid,
        type: { type: 'string', enum: ['income', 'expense'] },
        signedMinor: canonicalMinor,
        description,
        transactionDate: businessDate,
        dueDate: businessDate,
        count: installmentOrdinal,
    },
};
/**
 * `installments.edit`: one selected member, or that member and its eligible forward set.
 *
 * The writable fields are exactly the ones a forward edit may propagate (INST-05: description,
 * account, amount, and the optional category a later phase adds); `dueDate`, `paid` and `notes` are
 * absent on purpose, so a request carrying one is refused rather than applied to the wrong members.
 */
export const installmentsEditRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'groupId', 'fromEntryId', 'scope', 'fields'],
    properties: {
        action: { type: 'string', enum: ['installments.edit'] },
        groupId: uuid,
        fromEntryId: uuid,
        scope: { type: 'string', enum: ['single', 'forward'] },
        fields: {
            type: 'object',
            additionalProperties: false,
            minProperties: 1,
            properties: {
                signedMinor: canonicalMinor,
                description,
                /** A member's account may move, same-currency and same-role only (INST-04). */
                accountId: uuid,
            },
        },
    },
};
/** `installments.delete`: the explicit member selection to remove, never a whole group. */
export const installmentsDeleteRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'groupId', 'entryIds'],
    properties: {
        action: { type: 'string', enum: ['installments.delete'] },
        groupId: uuid,
        entryIds: { type: 'array', minItems: 1, maxItems: 120, items: uuid },
    },
};
/**
 * `POST /mutations`: commit the stored preview, unchanged and confirmed.
 */
export const mutationCommitRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['previewId', 'confirm'],
    properties: {
        previewId: uuid,
        confirm: { type: 'boolean', const: true },
        /**
         * The impact acknowledgement (P7-T1). A lifecycle commit whose stored impact removes a figure
         * that is already effective has to acknowledge it, so a paid or effective row cannot be
         * destroyed by a commit that never read what that does. It is optional and ignored by the
         * actions that have no impact to acknowledge.
         */
        impactAcknowledged: { type: 'boolean' },
    },
};
/** One installment group, in the resource envelope — a named, `$id`-bearing browser validator. */
export const ledgerInstallmentGroupEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-installment-group-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerInstallmentGroupEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: installmentGroupSchema, meta: ledgerMetaSchema },
};
/** One mutation preview, in the resource envelope — a named, `$id`-bearing browser validator. */
export const ledgerMutationPreviewEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-mutation-preview-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerMutationPreviewEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: mutationPreviewSchema, meta: ledgerMetaSchema },
};
/* -------------------------------------------------------------------- transfers --- */
/**
 * The transfer wire shapes (P6-T1).
 *
 * Sources: `IMPLEMENTATION-CONTRACTS.md` §5 (the central `transfer.create/update/complete/delete`
 * actions and the review-required spine), §3 (positive source magnitude normalized once to two
 * signed legs, same-currency equality, no direction-plus-money duplicate) and `REQUIREMENTS.md`
 * TRF-01/TRF-02 (exactly two legs, one operation, planned/completed state).
 *
 * Every object sets `additionalProperties: false`, so a `rate`, a `destinationMinor`, a
 * `currency` or an `fxInputMode` on a same-currency request is a **rejection** rather than a
 * silently dropped field — the vocabulary this card has no way to act on cannot ride along.
 */
/** Which side of the pair a leg is. */
export const TRANSFER_LEG_SIDES = ['outgoing', 'incoming'];
// `TRANSFER_STATUSES`, `transferStatus` and the transfer schema's own status member are declared
// near the top of this file: the entry read carries a leg's pair status too (P6-T3).
const transferSide = { type: 'string', enum: [...TRANSFER_LEG_SIDES] };
const accountKind = { type: 'string', enum: ['cash', 'card'] };
/* ------------------------------------------------------------------------ FX --- */
/**
 * The cash-to-cash FX shapes (P7-T3).
 *
 * Sources: `DECISION-REGISTER.md` "FX precision" (the operator's approved value, 2026-10-15) and
 * `IMPLEMENTATION-CONTRACTS.md` §3, quoted in `@saldo/domain`'s `fx.ts`. The rate is a **string**,
 * never a JSON number, for the same reason money is: `0.1 + 0.2` is not `0.3`, and a rate that
 * arrives as a float has already lost the digits the representation exists to keep.
 */
/** Which position the caller states: the rate, or the destination amount. */
export const FX_INPUT_MODES = ['rate', 'destination'];
/** Plain positive decimal text, at most 12 integer and 12 fractional digits (§3). */
export const SUPPLIED_RATE_PATTERN = '^(?:0|[1-9][0-9]{0,11})(?:\\.[0-9]{1,12})?$';
const fxInputMode = { type: 'string', enum: [...FX_INPUT_MODES] };
const suppliedRate = { type: 'string', pattern: SUPPLIED_RATE_PATTERN };
const nullableSuppliedRate = {
    type: ['string', 'null'],
    pattern: SUPPLIED_RATE_PATTERN,
};
const rateScale = { type: ['integer', 'null'], minimum: 0, maximum: 12 };
const nullableFxInputMode = { type: ['string', 'null'], enum: [...FX_INPUT_MODES, null] };
const rateCoefficient = { type: ['string', 'null'], pattern: '^[1-9][0-9]{0,23}$' };
const displayRate = { type: 'string', pattern: '^[0-9]+\\.[0-9]{6}$' };
/**
 * The FX metadata of a cross-currency pair, as a read returns it.
 *
 * It is **absent** on a same-currency pair rather than present-and-defaulted: a `rate` of `1` is
 * exactly the assumed parity the contract refuses, so there is no shape in which one could be
 * read. Direction is stated as its own label ("destination units per source unit", FX-05) and the
 * six-place figure is returned beside an approximation marker, so a client cannot print the
 * display as if it were the money (the two signed legs are, and always remain, the money).
 */
export const transferFxSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'inputMode',
        'sourceCurrency',
        'destinationCurrency',
        'directionLabel',
        'suppliedRate',
        'rateCoefficient',
        'rateScale',
        'displayRate',
        'displayRateApproximate',
    ],
    properties: {
        inputMode: fxInputMode,
        sourceCurrency: currency,
        destinationCurrency: currency,
        directionLabel: { type: 'string', minLength: 1, maxLength: 32 },
        /** The exact supplied rate text in `rate` mode; `null` in `destination` mode. */
        suppliedRate: nullableSuppliedRate,
        /** `numeric(24,0)` as a string, and the stored scale: the exact representation. */
        rateCoefficient,
        rateScale,
        /** Six fractional places, half-up. Never an operand of either leg. */
        displayRate,
        /** True when six places do not capture the exact ratio. */
        displayRateApproximate: { type: 'boolean' },
    },
};
/** One authoritative leg, as a transfer read returns it. */
export const transferLegSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['entryId', 'accountId', 'accountKind', 'side', 'signedMinor', 'currency', 'revision'],
    properties: {
        entryId: uuid,
        accountId: uuid,
        accountKind,
        side: transferSide,
        signedMinor: canonicalMinor,
        currency,
        revision: positiveInteger,
    },
};
/** A transfer parent and its two ordered legs; the parent carries no money of its own. */
export const transferSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'status',
        'transferDate',
        'description',
        'currency',
        'revision',
        'canceled',
        'legs',
    ],
    properties: {
        id: uuid,
        status: transferStatus,
        transferDate: businessDate,
        description,
        currency,
        revision: positiveInteger,
        /** True once the whole pair is canceled/deleted; the legs stay inspectable. */
        canceled: { type: 'boolean' },
        /** Present on a cross-currency pair only; absent on a same-currency one (no implied parity). */
        fx: transferFxSchema,
        legs: { type: 'array', maxItems: 2, minItems: 2, items: transferLegSchema },
    },
};
/** One leg as a **preview** shows it: an identity only when the row already exists. */
export const transferPreviewLegSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['accountId', 'accountKind', 'side', 'signedMinor', 'currency'],
    properties: {
        accountId: uuid,
        accountKind,
        side: transferSide,
        signedMinor: canonicalMinor,
        currency,
        entryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
    },
};
/**
 * A stored transfer preview (`POST /mutation-previews`, §4/§5).
 *
 * It carries the exact pair the caller would commit, the parent revision it was computed at, the
 * business day the eligibility was computed at and an expiry — so the commit can be refused as
 * stale instead of silently recomputing a different pair.
 */
export const transferPreviewSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'action',
        'transferId',
        'revision',
        'businessToday',
        'expiresAt',
        'status',
        'transferDate',
        'description',
        'currency',
        'canceled',
        'legs',
        'affectedEntryIds',
    ],
    properties: {
        id: uuid,
        action: {
            type: 'string',
            enum: [
                'transfer.create',
                'transfer.fxCreate',
                'transfer.update',
                'transfer.complete',
                'transfer.delete',
            ],
        },
        transferId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        revision: { type: ['string', 'null'], pattern: POSITIVE_INTEGER_PATTERN },
        businessToday: businessDate,
        expiresAt: { type: 'string', minLength: 1, maxLength: 64 },
        status: transferStatus,
        transferDate: businessDate,
        description,
        currency,
        /** The pair's state after the previewed action: a deletion previews `true`. */
        canceled: { type: 'boolean' },
        /** Present on a cross-currency pair only, exactly as the committed read would carry it. */
        fx: transferFxSchema,
        legs: { type: 'array', maxItems: 2, items: transferPreviewLegSchema },
        affectedEntryIds: { type: 'array', maxItems: 2, items: uuid },
    },
};
/**
 * `transfer.create`: a positive source magnitude, normalized once to the two signed legs.
 *
 * `status` is explicit — a plan and a completed movement are different intents, and both carry the
 * same shared date. No FX vocabulary appears here, because a same-currency pair has no rate.
 */
export const transferCreateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'action',
        'description',
        'transferDate',
        'status',
        'sourceAccountId',
        'destinationAccountId',
        'sourceMinor',
    ],
    properties: {
        action: { type: 'string', enum: ['transfer.create'] },
        description,
        transferDate: businessDate,
        status: transferStatus,
        sourceAccountId: uuid,
        destinationAccountId: uuid,
        sourceMinor: canonicalMinor,
    },
};
/** `transfer.update`: a correction of the pair's metadata and its two legs together. */
export const transferUpdateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'transferId', 'fields'],
    properties: {
        action: { type: 'string', enum: ['transfer.update'] },
        transferId: uuid,
        fields: {
            type: 'object',
            additionalProperties: false,
            minProperties: 1,
            properties: {
                description,
                transferDate: businessDate,
                sourceAccountId: uuid,
                destinationAccountId: uuid,
                sourceMinor: canonicalMinor,
            },
        },
    },
};
/** `transfer.complete`: the chosen shared completion date; both legs move with it. */
export const transferCompleteRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'transferId', 'transferDate'],
    properties: {
        action: { type: 'string', enum: ['transfer.complete'] },
        transferId: uuid,
        transferDate: businessDate,
    },
};
/** `transfer.delete`: cancel the whole pair, never a single leg. */
export const transferDeleteRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'transferId'],
    properties: {
        action: { type: 'string', enum: ['transfer.delete'] },
        transferId: uuid,
    },
};
/**
 * `transfer.fxCreate`: a cross-currency **cash→cash** pair, in exactly one stated mode.
 *
 * It is its own action rather than an optional half of `transfer.create`, and that is the
 * structural part of "no implicit parity": a same-currency request carrying `fxInputMode`,
 * `suppliedRate` or `destinationMinor` fails the schema above (`additionalProperties: false`), so
 * the FX vocabulary cannot ride along on a pair that has no rate. Here, `sourceMinor` is the
 * authoritative outgoing magnitude, `fxInputMode` is required, and the operands are optional
 * **because they are mode-dependent** — which of them must be present is a value rule the domain
 * states (`planFxTransfer`), so a request that names a mode without its operand is refused with a
 * named reason rather than silently defaulted.
 */
export const transferFxCreateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'action',
        'description',
        'transferDate',
        'status',
        'sourceAccountId',
        'destinationAccountId',
        'sourceMinor',
        'fxInputMode',
    ],
    properties: {
        action: { type: 'string', enum: ['transfer.fxCreate'] },
        description,
        transferDate: businessDate,
        status: transferStatus,
        sourceAccountId: uuid,
        destinationAccountId: uuid,
        sourceMinor: canonicalMinor,
        fxInputMode,
        /** Required in `rate` mode; refused alongside a supplied destination. */
        suppliedRate,
        /** Required in `destination` mode; refused alongside a supplied rate. */
        destinationMinor: canonicalMinor,
    },
};
/**
 * One account pair the owner has already used, offered for **explicit** reuse (TRF-06).
 *
 * It carries what the surface must show for the reviewer to judge the reuse — the pair, the last
 * date, how often it was used, and the last mode/amounts/rate — and it carries no default that
 * applies itself: a rate here is a *previous* rate, and nothing on this wire shape fills a form
 * without the reviewer choosing it.
 */
export const transferPairSuggestionSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'sourceAccountId',
        'sourceAccountName',
        'destinationAccountId',
        'destinationAccountName',
        'sourceCurrency',
        'destinationCurrency',
        'lastTransferDate',
        'lastStatus',
        'useCount',
        'lastSourceMinor',
        'lastDestinationMinor',
        'lastInputMode',
        'lastSuppliedRate',
    ],
    properties: {
        sourceAccountId: uuid,
        sourceAccountName: { type: 'string', minLength: 1, maxLength: 120 },
        destinationAccountId: uuid,
        destinationAccountName: { type: 'string', minLength: 1, maxLength: 120 },
        sourceCurrency: currency,
        destinationCurrency: currency,
        lastTransferDate: businessDate,
        lastStatus: transferStatus,
        useCount: { type: 'integer', minimum: 1 },
        lastSourceMinor: canonicalMinor,
        lastDestinationMinor: canonicalMinor,
        /** `null` for a same-currency pair, which has no rate to review. */
        lastInputMode: nullableFxInputMode,
        lastSuppliedRate: nullableSuppliedRate,
    },
};
/**
 * The transfer reads and the stored payment preview, in the same envelopes the rest of the
 * ledger answers with (P6-T2).
 *
 * These are the browser's own validators: the payments panel reads a transfer, lists an
 * account's transfers and confirms a stored `transfer.*` preview, and each of those
 * responses is validated with the precompiled function for its `$id` rather than being
 * rendered unchecked — the same reason the P4-T2 envelopes exist (`validator.ts`,
 * `strict-ajv.ts`). Regenerate with `npm run generate` after any change here.
 */
/** One transfer and its two ordered legs, in the resource envelope. */
export const ledgerTransferEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-transfer-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerTransferEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: transferSchema, meta: ledgerMetaSchema },
};
/** One owner's (or one account's) transfers, in the collection envelope. */
export const ledgerTransfersEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-transfers-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerTransfersEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: {
        data: {
            type: 'object',
            additionalProperties: false,
            required: ['transfers'],
            properties: { transfers: { type: 'array', maxItems: 200, items: transferSchema } },
        },
        meta: ledgerMetaSchema,
    },
};
/** A stored transfer preview, in the resource envelope. */
export const ledgerTransferPreviewEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-transfer-preview-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerTransferPreviewEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: transferPreviewSchema, meta: ledgerMetaSchema },
};
/**
 * The account pairs the owner has used, in the collection envelope (P7-T3): the read behind
 * "reviewed account-pair reuse". A journey that offers a repeated pair renders only what this
 * validator accepted, so the surface cannot invent a pair or a rate it was never served.
 */
export const ledgerTransferPairSuggestionsEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-transfer-pair-suggestions-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerTransferPairSuggestionsEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: {
        data: {
            type: 'object',
            additionalProperties: false,
            required: ['suggestions'],
            properties: {
                suggestions: { type: 'array', maxItems: 50, items: transferPairSuggestionSchema },
            },
        },
        meta: ledgerMetaSchema,
    },
};
/* ------------------------------------------------- categories and lifecycle (P7-T1) --- */
/**
 * The category and entry-lifecycle wire shapes (P7-T1).
 *
 * Sources: `DATABASE-PROPOSAL.md` §2 (a category is a label with a reversible `archived_at`
 * and no stored total), §3 ("one optional category", "referenced accounts/categories cannot be
 * hard-deleted"), `IMPLEMENTATION-CONTRACTS.md` §4/§5 (the review-required preview spine and
 * the `{data, meta}` envelope) and `DECISION-REGISTER.md` "Correction provenance".
 *
 * The two properties that carry the acceptance are structural rather than handler-side:
 *
 *   - every object sets `additionalProperties: false`, so a duplicate request carrying a
 *     `paid`, a `transferId` or an `installmentGroupId` is a **rejection** rather than a field a
 *     handler silently drops — the special identities the clause names cannot ride along;
 *   - `categoryId` is `["string","null"]` on the entry patch, so *uncategorized* is expressible
 *     as a deliberate value and not as the absence of a field.
 */
/** One category as the API returns it: its label, its reversible visibility and its derived total. */
export const categorySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['id', 'name', 'archived', 'revision', 'entryCount', 'totals'],
    properties: {
        id: uuid,
        name: { type: 'string', minLength: 1, maxLength: 120 },
        archived: { type: 'boolean' },
        revision: positiveInteger,
        /** How many entries carry it, canceled ones excluded — a derivation, never a stored count. */
        entryCount: { type: 'integer', minimum: 0 },
        /**
         * The category's whole figure, **per currency**: an entry's amount is counted once however
         * many entries share the category, an archived category keeps its totals, and two currencies
         * are never added together (an account's history is summed in its own native currency).
         */
        totals: { type: 'array', maxItems: 3, items: moneySchema },
    },
};
/** Creating a category: its name. It is created active. */
export const categoryCreateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['name'],
    properties: { name: { type: 'string', minLength: 1, maxLength: 120 } },
};
/**
 * Editing a category: rename and/or the reversible archive.
 *
 * `archived` is a boolean rather than a one-way action, so *unhide* is the same field with the
 * other value — the archive is reversible by the shape of the request, not by a second route.
 */
export const categoryPatchRequestSchema = {
    type: 'object',
    additionalProperties: false,
    minProperties: 1,
    properties: {
        name: { type: 'string', minLength: 1, maxLength: 120 },
        archived: { type: 'boolean' },
    },
};
/** One reference that would block a deletion. `kind` names what it is without naming the row. */
export const referenceBlockerSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['kind', 'count'],
    properties: {
        kind: {
            type: 'string',
            enum: ['entry', 'account', 'category', 'installmentGroup', 'transfer'],
        },
        /** How many rows reference the target. Never the rows themselves. */
        count: { type: 'integer', minimum: 1 },
    },
};
/** The consequential impact a lifecycle preview shows before the commit. */
export const entryImpactSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'accountId',
        'currency',
        'signedMinor',
        'balanceDeltaMinor',
        'paid',
        'effective',
        'requiresConfirmation',
        'references',
    ],
    properties: {
        accountId: uuid,
        currency,
        /** The entry's own signed amount, unchanged by the previewed action. */
        signedMinor: canonicalMinor,
        /** How the account's derived balance moves: the negative of the contribution removed. */
        balanceDeltaMinor: canonicalMinor,
        paid: { type: ['boolean', 'null'] },
        /** Whether the entry currently contributes to any figure under the eligibility rule. */
        effective: { type: 'boolean' },
        /**
         * True when the action destroys a figure that is already effective: a paid cash entry, a
         * card entry at or before Today, or an active opening. Those commits carry the impact
         * confirmation; a pending row's does not.
         */
        requiresConfirmation: { type: 'boolean' },
        /**
         * The rows the action touches or the deletion would be blocked by — the source leg's pair,
         * the installment group, the references a category or account deletion is refused for.
         */
        references: { type: 'array', maxItems: 16, items: referenceBlockerSchema },
    },
};
/** The lifecycle actions the review-required spine stores (P7-T1). */
export const ENTRY_LIFECYCLE_ACTIONS = ['entry.delete', 'entry.duplicate'];
/**
 * A stored lifecycle preview (`POST /mutation-previews`, §4/§5).
 *
 * It carries the entry's revision at preview time, the business day the impact was computed at
 * and an expiry, so the commit can be refused as stale instead of recomputing a different
 * impact.
 */
export const entryLifecyclePreviewSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'action',
        'entryId',
        'revision',
        'businessToday',
        'expiresAt',
        'canceled',
        'impact',
    ],
    properties: {
        id: uuid,
        action: { type: 'string', enum: [...ENTRY_LIFECYCLE_ACTIONS] },
        entryId: uuid,
        /** The entry's revision the preview was computed at; always present, because the row exists. */
        revision: positiveInteger,
        businessToday: businessDate,
        expiresAt: { type: 'string', minLength: 1, maxLength: 64 },
        /** The entry's state after the previewed action: a deletion previews `true`. */
        canceled: { type: 'boolean' },
        impact: entryImpactSchema,
    },
};
/** `entry.delete`: the row to cancel, named once. */
export const entryDeleteRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'entryId'],
    properties: {
        action: { type: 'string', enum: ['entry.delete'] },
        entryId: uuid,
    },
};
/**
 * `entry.duplicate`: the row to copy as a new ordinary entry.
 *
 * The request carries **no** field a special identity could arrive in — no `paid`, no
 * `transferId`, no `installmentGroupId`, no `categoryId` override — so "the duplicate cannot
 * copy a special identity" is enforced by the request shape, not only by the service.
 */
export const entryDuplicateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'entryId'],
    properties: {
        action: { type: 'string', enum: ['entry.duplicate'] },
        entryId: uuid,
    },
};
/** How an entry contributes to a figure, derived for one business day. */
export const entryEligibilitySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['effective', 'reason'],
    properties: {
        effective: { type: 'boolean' },
        /**
         * Why it does or does not contribute, in the vocabulary the eligibility rule uses. `null`
         * when the entry is effective.
         */
        reason: {
            type: ['string', 'null'],
            enum: [
                null,
                'canceled',
                'cash-unpaid',
                'future',
                'pair-not-completed',
                'pair-canceled',
            ],
        },
    },
};
/**
 * One autocomplete suggestion: a description the owner has already used, with the fields a
 * similar entry would reuse and the last values they carried.
 *
 * It is a **read**, never a write: reusing a suggestion still goes through the ordinary create
 * or the reviewed duplicate.
 */
export const entrySuggestionSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'description',
        'occurrences',
        'lastUsedDate',
        'accountId',
        'signedMinor',
        'currency',
        'categoryId',
    ],
    properties: {
        description: { type: 'string', minLength: 1, maxLength: 500 },
        occurrences: { type: 'integer', minimum: 1 },
        lastUsedDate: businessDate,
        accountId: uuid,
        signedMinor: canonicalMinor,
        currency,
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
    },
};
/** The optional substring, the optional account scope, and a bounded page size. */
export const entrySuggestionQuerySchema = {
    type: 'object',
    additionalProperties: false,
    properties: {
        q: { type: 'string', maxLength: 500 },
        accountId: uuid,
        limit: { type: 'string', pattern: '^[1-9][0-9]{0,2}$' },
    },
};
/** One category, in the resource envelope — a named, `$id`-bearing browser validator. */
export const ledgerCategoryEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-category-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerCategoryEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: categorySchema, meta: ledgerMetaSchema },
};
/** One owner's categories, in the collection envelope. */
export const ledgerCategoriesEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-categories-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerCategoriesEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: {
        data: {
            type: 'object',
            additionalProperties: false,
            required: ['categories'],
            properties: { categories: { type: 'array', maxItems: 500, items: categorySchema } },
        },
        meta: ledgerMetaSchema,
    },
};
/** A stored lifecycle preview, in the resource envelope. */
export const ledgerEntryLifecyclePreviewEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-entry-lifecycle-preview-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerEntryLifecyclePreviewEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: entryLifecyclePreviewSchema, meta: ledgerMetaSchema },
};
/** Autocomplete suggestions, in the collection envelope. */
export const ledgerEntrySuggestionsEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-entry-suggestions-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerEntrySuggestionsEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: {
        data: {
            type: 'object',
            additionalProperties: false,
            required: ['suggestions'],
            properties: { suggestions: { type: 'array', maxItems: 50, items: entrySuggestionSchema } },
        },
        meta: ledgerMetaSchema,
    },
};
/* ---------------------------------------------- recurrence and its terminal stop (P8-T2) --- */
/**
 * The recurrence wire shapes (P8-T2): the schedule as the reads report it, the occurrence
 * classification a stop preview shows, the create/stop requests and the two stored previews.
 *
 * Sources: `TECHNICAL-SPECIFICATION.md` §6 (the revision-bound stop preview, the exact
 * cancellation set with its exclusions, edited exceptions kept out of the default set, protected
 * paid/effective/overdue history, the terminal stop-only alternative, the reviewed new-template
 * overlap), `REQUIREMENTS.md` `REC-02` and `IMPLEMENTATION-CONTRACTS.md` §4/§5.
 *
 * Two properties are shapes rather than conventions: an occurrence's `disposition` is one of the
 * seven the plan names, so a surface cannot invent a bucket; and `selectedEntryIds` is a list of
 * **ids the caller typed**, never a filter, so "explicit selected IDs" is the only way an edited
 * exception can enter a cancellation set.
 */
export const RECURRENCE_ACTIONS = ['recurrence.create', 'recurrence.stop'];
/** The three reviewed frequencies; there is no annual one (`TECHNICAL-SPECIFICATION.md` §6). */
export const RECURRENCE_FREQUENCIES = ['monthly', 'weekly', 'biweekly'];
/** A stop is terminal: a schedule is active or stopped, and stopped is not a pause. */
export const RECURRENCE_TEMPLATE_STATUSES = ['active', 'stopped'];
/** The default stop and its explicit alternative (`REC-02`). */
export const RECURRENCE_STOP_MODES = ['stop-and-cancel', 'stop-only'];
/** The dispositions the classification reports, in the specification's own vocabulary. */
export const OCCURRENCE_DISPOSITIONS = [
    'eligible-forward',
    'edited-exception',
    'paid',
    'effective',
    'overdue',
    'canceled',
    'deleted',
];
/** The fields an occurrence edit can touch, as the classification names them. */
export const OCCURRENCE_EDIT_FIELDS = [
    'amount',
    'description',
    'category',
    'account',
    'dueDate',
    'transactionDate',
];
/** One recurring schedule as the reads report it. Dates are business DATE text, never `Date`. */
export const recurrenceTemplateSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'accountId',
        'categoryId',
        'description',
        'signedMinor',
        'currency',
        'accountKind',
        'frequency',
        'anchorDate',
        'endDate',
        'status',
        'generatedThrough',
        'revision',
    ],
    properties: {
        id: uuid,
        accountId: uuid,
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        description,
        /** The occurrence's signed value; each occurrence copies it into its own entry. */
        signedMinor: canonicalMinor,
        currency,
        accountKind: { type: 'string', enum: ['cash', 'card'] },
        frequency: { type: 'string', enum: [...RECURRENCE_FREQUENCIES] },
        /** The **original** anchor: clamping a short month never rewrites it. */
        anchorDate: businessDate,
        endDate: nullableBusinessDate,
        status: { type: 'string', enum: [...RECURRENCE_TEMPLATE_STATUSES] },
        generatedThrough: nullableBusinessDate,
        revision: positiveInteger,
    },
};
/** One generated occurrence, classified, as a read or a stop preview shows it. */
export const recurrenceOccurrenceSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'ordinal',
        'occurrenceDate',
        'entryId',
        'disposition',
        'editedFields',
        'accountId',
        'signedMinor',
        'description',
        'dueDate',
        'paid',
        'revision',
    ],
    properties: {
        ordinal: { type: 'integer', minimum: 1 },
        occurrenceDate: businessDate,
        /** `null` for a deleted occurrence: the identity is a tombstone and the ordinal stays spent. */
        entryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        disposition: { type: 'string', enum: [...OCCURRENCE_DISPOSITIONS] },
        editedFields: {
            type: 'array',
            maxItems: 6,
            items: { type: 'string', enum: [...OCCURRENCE_EDIT_FIELDS] },
        },
        accountId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        signedMinor: { type: ['string', 'null'], pattern: CANONICAL_MINOR_PATTERN },
        description: { type: ['string', 'null'], maxLength: 500 },
        dueDate: { type: ['string', 'null'], pattern: BUSINESS_DATE_PATTERN },
        paid: { type: ['boolean', 'null'] },
        revision: { type: ['string', 'null'], pattern: POSITIVE_INTEGER_PATTERN },
    },
};
/** An occurrence the stop will not cancel, with the reason it will not. */
export const recurrenceExclusionSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['ordinal', 'entryId', 'reason'],
    properties: {
        ordinal: { type: 'integer', minimum: 1 },
        /** `null` for a tombstone, which has no entry to name. */
        entryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        reason: { type: 'string', enum: [...OCCURRENCE_DISPOSITIONS] },
    },
};
const recurrenceIdList = { type: 'array', maxItems: 2000, items: uuid };
/**
 * The classification three shapes share: the occurrence/eligible-forward preview, the detail read
 * and the stop preview. `cancellableEntryIds` is the default set — exactly what a stop with no
 * edited exceptions named would cancel — and `editedExceptionEntryIds` is what it withholds.
 */
const recurrenceClassificationProperties = {
    occurrences: { type: 'array', maxItems: 2000, items: recurrenceOccurrenceSchema },
    cancellableEntryIds: recurrenceIdList,
    editedExceptionEntryIds: recurrenceIdList,
    excluded: { type: 'array', maxItems: 2000, items: recurrenceExclusionSchema },
};
/** `GET /recurrences/{id}`: the schedule and the classification of its occurrences. */
export const recurrenceDetailSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['template', 'occurrences', 'cancellableEntryIds', 'editedExceptionEntryIds', 'excluded'],
    properties: { template: recurrenceTemplateSchema, ...recurrenceClassificationProperties },
};
/** One occurrence a `recurrence.create` preview would write. */
export const recurrencePreviewOccurrenceSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['ordinal', 'occurrenceDate', 'signedMinor', 'currency'],
    properties: {
        ordinal: { type: 'integer', minimum: 1 },
        occurrenceDate: businessDate,
        signedMinor: canonicalMinor,
        currency,
    },
};
/**
 * One retained entry that already falls on a date the new schedule would occupy.
 *
 * The match is account and due date; the amount is reported, never compared, so a different amount
 * is a warning too — "reviewed, never silently merged" (`TECHNICAL-SPECIFICATION.md` §6, F-22).
 */
export const recurrenceOverlapSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['occurrenceDate', 'entryId', 'description', 'signedMinor', 'currency', 'generated'],
    properties: {
        occurrenceDate: businessDate,
        entryId: uuid,
        description: { type: 'string', minLength: 1, maxLength: 500 },
        signedMinor: canonicalMinor,
        currency,
        /** True when a recurrence identity generated it, false when it was entered by hand. */
        generated: { type: 'boolean' },
    },
};
/** `recurrence.create`: the schedule to add. Its fill does not begin before the window. */
export const recurrenceCreateRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'accountId', 'description', 'signedMinor', 'frequency', 'anchorDate'],
    properties: {
        action: { type: 'string', enum: ['recurrence.create'] },
        accountId: uuid,
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        description,
        signedMinor: canonicalMinor,
        frequency: { type: 'string', enum: [...RECURRENCE_FREQUENCIES] },
        anchorDate: businessDate,
        endDate: nullableBusinessDate,
    },
};
/**
 * `recurrence.stop`: the mode, and the edited exceptions the caller explicitly names.
 *
 * `selectedEntryIds` is deliberately a list of ids and nothing else: a scope, a filter or a
 * date range would be the "silently swept" selection the acceptance clause forbids.
 */
export const recurrenceStopRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'templateId', 'mode'],
    properties: {
        action: { type: 'string', enum: ['recurrence.stop'] },
        templateId: uuid,
        mode: { type: 'string', enum: [...RECURRENCE_STOP_MODES] },
        selectedEntryIds: { type: 'array', maxItems: 2000, items: uuid },
    },
};
/** What a `recurrence.create` preview shows: the occurrences it would write, and any overlap. */
export const recurrenceCreatePreviewSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'action',
        'templateId',
        'revision',
        'businessToday',
        'expiresAt',
        'accountId',
        'currency',
        'occurrences',
        'overlap',
        'note',
        'requiresConfirmation',
    ],
    properties: {
        id: uuid,
        action: { type: 'string', enum: ['recurrence.create'] },
        /** Always `null` for a creation: there is no target to version yet. */
        templateId: { type: 'null' },
        revision: { type: 'null' },
        businessToday: businessDate,
        expiresAt: { type: 'string', minLength: 1, maxLength: 64 },
        accountId: uuid,
        currency,
        occurrences: { type: 'array', maxItems: 2000, items: recurrencePreviewOccurrenceSchema },
        overlap: { type: 'array', maxItems: 2000, items: recurrenceOverlapSchema },
        note: { type: 'string', minLength: 1, maxLength: 600 },
        /**
         * True when the schedule lands on dates that already carry entries. The commit then has to
         * acknowledge the warning, so "reviewed, never silently merged" is a gate and not a caption.
         */
        requiresConfirmation: { type: 'boolean' },
    },
};
/** What a `recurrence.stop` preview shows: the exact cancellation set, and the exclusions. */
export const recurrenceStopPreviewSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'id',
        'action',
        'templateId',
        'revision',
        'businessToday',
        'expiresAt',
        'mode',
        'occurrences',
        'cancellableEntryIds',
        'editedExceptionEntryIds',
        'excluded',
        'selectedEntryIds',
        'cancelledEntryIds',
    ],
    properties: {
        id: uuid,
        action: { type: 'string', enum: ['recurrence.stop'] },
        templateId: uuid,
        /** The template's revision at preview time; a commit refuses anything else as stale. */
        revision: positiveInteger,
        businessToday: businessDate,
        expiresAt: { type: 'string', minLength: 1, maxLength: 64 },
        mode: { type: 'string', enum: [...RECURRENCE_STOP_MODES] },
        ...recurrenceClassificationProperties,
        /** The edited exceptions this request named, after the service checked each one. */
        selectedEntryIds: recurrenceIdList,
        /** Exactly what the commit cancels: empty under `stop-only`. */
        cancelledEntryIds: recurrenceIdList,
    },
};
/** The two recurrence previews, discriminated by `action`, like every other preview body. */
export const recurrencePreviewSchema = {
    oneOf: [recurrenceCreatePreviewSchema, recurrenceStopPreviewSchema],
};
/** One schedule with its classified occurrences, in the resource envelope — a browser validator. */
export const ledgerRecurrenceEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-recurrence-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerRecurrenceEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: recurrenceDetailSchema, meta: ledgerMetaSchema },
};
/**
 * The owner's schedules, as a collection body.
 *
 * A read, so it carries no `If-Match` and no idempotency key, and its shape is an object with a
 * `recurrences` member rather than a bare array — a top-level `data` that is an object leaves room
 * for a second member without breaking every client.
 */
export const recurrencesBodySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['recurrences'],
    properties: {
        recurrences: { type: 'array', maxItems: 200, items: recurrenceTemplateSchema },
    },
};
/** The schedules collection, in the collection envelope — a named, `$id`-bearing validator. */
export const ledgerRecurrencesEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-recurrences-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerRecurrencesEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: recurrencesBodySchema, meta: ledgerMetaSchema },
};
/**
 * The commit answer of a recurrence action: the schedule, in the resource envelope.
 *
 * A creation and a stop both answer with the template — the creation's is the one it just wrote,
 * with its initial window filled and its watermark set; the stop's is the same row, now terminal.
 * It is its own validator rather than the status code's, so a commit can never be parsed as
 * something the route does not answer.
 */
export const ledgerRecurrenceTemplateEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-recurrence-template-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerRecurrenceTemplateEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: recurrenceTemplateSchema, meta: ledgerMetaSchema },
};
/**
 * One recurrence preview, in the resource envelope — a named, `$id`-bearing browser validator.
 *
 * The `data` branch is the `oneOf` of the two previews, so a create preview can never be rendered
 * as a stop preview: they share the route and the envelope and nothing else.
 */
export const ledgerRecurrencePreviewEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-recurrence-preview-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerRecurrencePreviewEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: recurrencePreviewSchema, meta: ledgerMetaSchema },
};
/* ------------------------------------------- explicit-ID bulk actions (P8-T3) --- */
/**
 * The explicit-ID bulk wire shapes (P8-T3): the three central actions, the stored preview, and the
 * commit's answer.
 *
 * Sources: `REQUIREMENTS.md` `BAL-07` ("Bulk categorization and cash marking paid operate **only on
 * explicitly selected IDs** ... No all-pages, filter-wide selection/action or individual
 * transfer-leg/card-paid bulk mutation. A changed selection requires a new preview."), `SAFE-02`,
 * `SAFE-04`, `IMPLEMENTATION-CONTRACTS.md` §3 ("explicit bulk ≤500 IDs") and §5 (the central
 * actions list) and `FINANCIAL-ACCEPTANCE-EXAMPLES.md` F-30.
 *
 * Two properties are shapes rather than conventions, and both carry an acceptance clause:
 *
 *   - **the request has no field a filter could arrive in.** `entryIds` is a list of ids and
 *     nothing else — there is no `scope`, `accountId`, `filter`, `q`, `page` or `all` member, and
 *     `additionalProperties: false` makes one a rejection rather than a silently ignored extra. So
 *     "bulk applies to the exact selected ids, never to a query" is enforced by the request's own
 *     schema, not by the handler remembering to ignore something;
 *   - **the preview carries the rows it will touch, with their revisions.** A commit re-derives
 *     them and compares, so a selection whose membership or versions moved between the review and
 *     the commit is refused as stale instead of applying to a set the caller never saw.
 */
export const BULK_ACTIONS = ['bulk.pay', 'bulk.categorize', 'bulk.cancel'];
/** Why a named row cannot be touched by the requested action (`BULK_ROW_REFUSALS` in the domain). */
export const BULK_ROW_REFUSAL_REASONS = [
    'unavailable',
    'transfer-leg',
    'opening',
    'canceled',
    'card-paid-derived',
    'already-paid',
];
/** The bounded selection: at least one id, at most the contract's 500 (§3). */
const bulkEntryIds = { type: 'array', minItems: 1, maxItems: 500, items: uuid };
/** `bulk.pay`: the exact ids to mark paid, and the one date the money moved. */
export const bulkPayRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'entryIds', 'paidDate'],
    properties: {
        action: { type: 'string', enum: ['bulk.pay'] },
        entryIds: bulkEntryIds,
        /** The chosen payment date, applied to every row — the same edit the single cash pay makes. */
        paidDate: businessDate,
    },
};
/** `bulk.categorize`: the exact ids, and the one optional category (`null` clears it). */
export const bulkCategorizeRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'entryIds', 'categoryId'],
    properties: {
        action: { type: 'string', enum: ['bulk.categorize'] },
        entryIds: bulkEntryIds,
        /** Assign/replace/clear with **one** optional category; `null` is the deliberate clear. */
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
    },
};
/** `bulk.cancel`: the exact ids to cancel. Nothing else — no mode, no scope, no date. */
export const bulkCancelRequestSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['action', 'entryIds'],
    properties: {
        action: { type: 'string', enum: ['bulk.cancel'] },
        entryIds: bulkEntryIds,
    },
};
/** One selected row as the preview shows it: the row's own shape, plus its revision. */
export const bulkPreviewRowSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'entryId',
        'revision',
        'accountId',
        'accountKind',
        'type',
        'signedMinor',
        'currency',
        'description',
        'dueDate',
        'paid',
        'categoryId',
    ],
    properties: {
        entryId: uuid,
        /** The row's revision at preview time; the commit refuses a row that moved. */
        revision: positiveInteger,
        accountId: uuid,
        accountKind: { type: 'string', enum: ['cash', 'card'] },
        type: { type: 'string', enum: ['income', 'expense'] },
        signedMinor: canonicalMinor,
        currency,
        description: { type: 'string', minLength: 1, maxLength: 500 },
        dueDate: nullableBusinessDate,
        paid: { type: ['boolean', 'null'] },
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
    },
};
/** The account's recorded figure, as one bulk action moves it. */
export const bulkAccountDeltaSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['accountId', 'currency', 'deltaMinor'],
    properties: {
        accountId: uuid,
        currency,
        /** Signed: the recorded balance moves by exactly this. Zero when nothing is recorded yet. */
        deltaMinor: canonicalMinor,
    },
};
/**
 * What a bulk preview says the action would do, in one object whatever the action.
 *
 * Every member is present for every action, `null` where the action has no such value, so a client
 * renders one shape and cannot meet a missing field it has to guess at. `count` is the size of the
 * reviewed selection; the per-action counts say how many rows each effect covers; `changedCount` is
 * the categorisation's honest "how many actually move" — a row already in the named category is
 * touched by the transaction but its category does not change, and saying so is not the same as
 * saying it was skipped.
 */
export const bulkImpactSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'count',
        'paidCount',
        'cancelledCount',
        'categorisedCount',
        'changedCount',
        'categoryId',
        'paidDate',
        'accounts',
    ],
    properties: {
        count: { type: 'integer', minimum: 0, maximum: 500 },
        paidCount: { type: 'integer', minimum: 0, maximum: 500 },
        cancelledCount: { type: 'integer', minimum: 0, maximum: 500 },
        categorisedCount: { type: 'integer', minimum: 0, maximum: 500 },
        changedCount: { type: 'integer', minimum: 0, maximum: 500 },
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        paidDate: nullableBusinessDate,
        /** Per-account recorded-balance deltas; empty for a categorisation, which moves no figure. */
        accounts: { type: 'array', maxItems: 500, items: bulkAccountDeltaSchema },
    },
};
/** The stored bulk preview (`POST /mutation-previews`), which the commit replays unchanged. */
export const bulkPreviewSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['id', 'action', 'businessToday', 'expiresAt', 'entryIds', 'rows', 'impact'],
    properties: {
        id: uuid,
        action: { type: 'string', enum: [...BULK_ACTIONS] },
        /** The business day the review was taken at: changing Today invalidates the preview. */
        businessToday: businessDate,
        expiresAt: { type: 'string', minLength: 1, maxLength: 64 },
        /** The exact normalised selection: the same ids, deduplicated and in a stable order. */
        entryIds: bulkEntryIds,
        rows: { type: 'array', minItems: 1, maxItems: 500, items: bulkPreviewRowSchema },
        impact: bulkImpactSchema,
    },
};
/** What a bulk commit answers: the ids it wrote, and the same impact the preview stated. */
export const bulkCommitResultSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['entryIds', 'count', 'impact'],
    properties: {
        entryIds: bulkEntryIds,
        count: { type: 'integer', minimum: 0, maximum: 500 },
        impact: bulkImpactSchema,
    },
};
/**
 * One bulk preview, in the resource envelope — a named, `$id`-bearing browser validator.
 */
export const ledgerBulkPreviewEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-bulk-preview-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerBulkPreviewEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: bulkPreviewSchema, meta: ledgerMetaSchema },
};
/** One bulk commit's answer, in the resource envelope. */
export const ledgerBulkResultEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-bulk-result-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerBulkResultEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: bulkCommitResultSchema, meta: ledgerMetaSchema },
};
/**
 * The preview request: exactly one central action, discriminated by `action`.
 *
 * `oneOf` is what makes the discriminator real — a body that carries both a `count` and a `fields`
 * member, or a transfer field and an installment field, matches no branch and is refused as a
 * request, rather than being interpreted as one action and losing the extra field. It is declared
 * after every action's own request schema so that the bulk actions — added last — can be listed here
 * without a use-before-declaration.
 */
export const mutationPreviewRequestSchema = {
    oneOf: [
        installmentsCreateRequestSchema,
        installmentsEditRequestSchema,
        installmentsDeleteRequestSchema,
        transferCreateRequestSchema,
        transferFxCreateRequestSchema,
        transferUpdateRequestSchema,
        transferCompleteRequestSchema,
        transferDeleteRequestSchema,
        entryDeleteRequestSchema,
        entryDuplicateRequestSchema,
        recurrenceCreateRequestSchema,
        recurrenceStopRequestSchema,
        bulkPayRequestSchema,
        bulkCategorizeRequestSchema,
        bulkCancelRequestSchema,
    ],
};
/* ---------------------------------------------------- reports and forecast (P7-T2) --- */
/**
 * The report and forecast wire shapes (P7-T2).
 *
 * Sources: `REQUIREMENTS.md` §12 (RPT-01–RPT-06) and §11 (BAL-01, BAL-04, BAL-08);
 * `IMPLEMENTATION-CONTRACTS.md` §3 (canonical integers, business dates) and §5 (the `{data, meta}`
 * envelope).
 *
 * Three properties of these shapes carry the acceptance rather than leaving it to a handler:
 *
 *   - every money figure is a **canonical minor-unit string**, never a JSON number, so a report can
 *     be exact to the cent and no float enters the wire;
 *   - a report is a list of **per-currency blocks**, and the payload has **no field a cross-currency
 *     total could live in** (`RPT-05`, `FX-04`) — at most three, one per native currency;
 *   - the coverage of a forecast is `complete: false` with a `null` `completeThrough` and a required
 *     note, so a client cannot render a projection and leave the incompleteness unstated (`RPT-04`).
 */
/** The plan's three report scopes (P4-T2's month context, an explicit range, all periods). */
export const REPORT_SCOPE_KINDS = ['month', 'range', 'all'];
/** Which account roles a read includes (`BAL-08`). A scope, never a conversion. */
export const REPORT_ROLE_SCOPES = ['all', 'cash', 'card'];
const reportScopeKind = { type: 'string', enum: [...REPORT_SCOPE_KINDS] };
const reportRoleScope = { type: 'string', enum: [...REPORT_ROLE_SCOPES] };
const calendarMonth = { type: 'string', pattern: '^[0-9]{4}-(?:0[1-9]|1[0-2])$' };
/**
 * The report read's query.
 *
 * `scope` is required, so a caller always says which of the three it wants; `month`/`from`/`to` are
 * the bounds that scope needs, and a combination that does not match its scope is refused by the
 * route rather than silently reinterpreted. `cutoff` is the **explicit** date the recorded/pending
 * split is taken at (`BAL-02`: an after-today figure is never presented as available today);
 * omitted, it is the business day. `role` and `accountId` are the scope selection.
 */
export const reportQuerySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['scope'],
    properties: {
        scope: reportScopeKind,
        month: calendarMonth,
        from: businessDate,
        to: businessDate,
        role: reportRoleScope,
        accountId: uuid,
        cutoff: businessDate,
    },
};
/** The forecast read's query: one account, and the date the projection runs to. */
export const forecastQuerySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['accountId'],
    properties: { accountId: uuid, through: businessDate, cutoff: businessDate },
};
/** The period a report was taken over, named back so a heading cannot outrun it. */
export const reportPeriodSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['kind', 'start', 'endExclusive', 'label'],
    properties: {
        kind: reportScopeKind,
        start: { type: ['string', 'null'], pattern: BUSINESS_DATE_PATTERN },
        endExclusive: { type: ['string', 'null'], pattern: BUSINESS_DATE_PATTERN },
        label: { type: 'string', minLength: 1, maxLength: 64 },
    },
};
/** Three money figures: income and expense as positive magnitudes, and their signed net. */
export const reportTotalsSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['incomeMinor', 'expenseMinor', 'netMinor'],
    properties: { incomeMinor: canonicalMinor, expenseMinor: canonicalMinor, netMinor: canonicalMinor },
};
/** Those three figures, split into the rows that are settled at the cutoff and everything else. */
export const incomeExpenseReportSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['incomeMinor', 'expenseMinor', 'netMinor', 'recorded', 'pending'],
    properties: {
        incomeMinor: canonicalMinor,
        expenseMinor: canonicalMinor,
        netMinor: canonicalMinor,
        recorded: reportTotalsSchema,
        pending: reportTotalsSchema,
    },
};
/**
 * One line of a category breakdown.
 *
 * `categoryId: null` is the *uncategorized* group — a first-class selection, not a missing value
 * (`CAT-02`). An archived category keeps its line and its totals (`CAT-01`, `F-27`).
 */
export const categoryReportRowSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['categoryId', 'label', 'archived', 'entryCount', 'totals'],
    properties: {
        categoryId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        label: { type: 'string', minLength: 1, maxLength: 120 },
        archived: { type: 'boolean' },
        entryCount: { type: 'integer', minimum: 0 },
        totals: incomeExpenseReportSchema,
    },
};
/** One native currency's report block. */
export const currencyReportBlockSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['currency', 'totals', 'categories'],
    properties: {
        currency,
        totals: incomeExpenseReportSchema,
        categories: { type: 'array', maxItems: 500, items: categoryReportRowSchema },
    },
};
/**
 * The report payload.
 *
 * `blocks` is the whole result: at most one block per native currency, and nowhere to put a figure
 * that adds two of them together. `roleScope` is reported back so a screen can label what it is
 * showing, and `accountId` is present only when the read was narrowed to one account.
 */
export const reportsBodySchema = {
    type: 'object',
    additionalProperties: false,
    required: ['period', 'cutoff', 'roleScope', 'accountId', 'blocks'],
    properties: {
        period: reportPeriodSchema,
        businessToday: businessDate,
        cutoff: businessDate,
        roleScope: reportRoleScope,
        accountId: { type: ['string', 'null'], pattern: UUID_PATTERN },
        blocks: { type: 'array', maxItems: 3, items: currencyReportBlockSchema },
    },
};
/**
 * What a forecast states about the recurrence generation behind it (P8-T3).
 *
 * Before the generation existed this shape could only say `complete: false` with a `null`
 * `completeThrough` and the reason `recurrence-not-implemented` — an honest statement about the
 * code. P8-T1 shipped the engine and P8-T3 made the read state the **measurement**: whether the
 * generation covered the horizon, and when it did not, the day it stops at. The shape therefore has
 * to admit both answers, and `complete: true` is no longer a shape error — it is what a covered
 * horizon honestly reads. What stays structural is the rule itself: `complete: false` always names
 * its boundary or says there is none, so a client cannot render a projection and quietly imply that
 * ungenerated commitments are zero (`REC-03`, `RPT-04`, F-08).
 */
export const forecastCoverageSchema = {
    type: 'object',
    additionalProperties: false,
    required: ['complete', 'completeThrough', 'reason', 'note'],
    properties: {
        complete: { type: 'boolean' },
        /** The last day generation has covered, inclusive; `null` when nothing was generated. */
        completeThrough: { type: ['string', 'null'], pattern: BUSINESS_DATE_PATTERN },
        reason: {
            type: 'string',
            enum: ['complete', 'gap', 'unknown'],
            description: '`complete`: every applicable template covers the horizon. `gap`: generation stops at ' +
                '`completeThrough`. `unknown`: a template has generated nothing, so there is no boundary.',
        },
        note: { type: 'string', minLength: 1, maxLength: 600 },
    },
};
/** The forecast's components, each a positive magnitude with its direction in the name. */
export const forecastComponentsSchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'recordedMinor',
        'pendingOutgoingMinor',
        'pendingIncomingMinor',
        'plannedOutgoingMinor',
        'plannedIncomingMinor',
        'futureCompletedOutgoingMinor',
        'futureCompletedIncomingMinor',
        'overdueOutgoingMinor',
        'overdueIncomingMinor',
        'overdueCount',
        'pendingCount',
        'plannedCount',
    ],
    properties: {
        recordedMinor: canonicalMinor,
        pendingOutgoingMinor: canonicalMinor,
        pendingIncomingMinor: canonicalMinor,
        plannedOutgoingMinor: canonicalMinor,
        plannedIncomingMinor: canonicalMinor,
        futureCompletedOutgoingMinor: canonicalMinor,
        futureCompletedIncomingMinor: canonicalMinor,
        overdueOutgoingMinor: canonicalMinor,
        overdueIncomingMinor: canonicalMinor,
        overdueCount: { type: 'integer', minimum: 0 },
        pendingCount: { type: 'integer', minimum: 0 },
        plannedCount: { type: 'integer', minimum: 0 },
    },
};
/** One account's forecast through the selected date. */
export const forecastBodySchema = {
    type: 'object',
    additionalProperties: false,
    required: [
        'accountId',
        'accountName',
        'currency',
        'kind',
        'archived',
        'cutoff',
        'through',
        'recordedMinor',
        'projectedMinor',
        'components',
        'coverage',
    ],
    properties: {
        accountId: uuid,
        accountName: { type: 'string', minLength: 1, maxLength: 120 },
        currency,
        kind: { type: 'string', enum: ['cash', 'card'] },
        /** An archived account still forecasts: archiving is visibility, never history (`ACC-03`). */
        archived: { type: 'boolean' },
        cutoff: businessDate,
        through: businessDate,
        recordedMinor: canonicalMinor,
        projectedMinor: canonicalMinor,
        components: forecastComponentsSchema,
        coverage: forecastCoverageSchema,
    },
};
/** The reports read, in the collection envelope — a named, `$id`-bearing browser validator. */
export const ledgerReportsEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-reports-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerReportsEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: reportsBodySchema, meta: ledgerMetaSchema },
};
/** One account's forecast, in the resource envelope. */
export const ledgerForecastEnvelopeSchema = {
    $id: 'https://saldo.local/schemas/ledger-forecast-envelope.json',
    $schema: 'http://json-schema.org/draft-07/schema#',
    title: 'LedgerForecastEnvelope',
    type: 'object',
    additionalProperties: false,
    required: ['data', 'meta'],
    properties: { data: forecastBodySchema, meta: ledgerMetaSchema },
};
//# sourceMappingURL=ledger.js.map